import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    Avatar,
    Divider,
    Paper,
    Grid,
    Badge,
    Fab,
    Menu,
    MenuItem,
    Tabs,
    Tab,
    Chip,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    Alert,
    Tooltip,
    Switch,
    FormControlLabel
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon,
    Phone as PhoneIcon,
    Add as AddIcon,
    Person as PersonIcon,
    History as HistoryIcon,
    Message as MessageIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Notifications as NotificationsIcon,
    Email as EmailIcon,
    ContactPhone as ContactPhoneIcon,
    SmartToy as BotIcon,
    EventNote as EventNoteIcon
} from '@mui/icons-material';
import axios from 'axios';

const TwilioConversations = ({ open, onClose }) => {
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [contacts, setContacts] = useState([]);
    const [callHistory, setCallHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [newConversationNumber, setNewConversationNumber] = useState('');
    const [showNewConversation, setShowNewConversation] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [contactDetails, setContactDetails] = useState({
        name: '',
        phone: '',
        email: '',
        notes: ''
    });
    const [reminderSettings, setReminderSettings] = useState({
        enabled: false,
        interval: '24',
        message: 'Hi there! Just following up on our conversation. Is there anything else I can help with?',
        type: 'manual'
    });
    const [isCreatingContact, setIsCreatingContact] = useState(false);
    const [isSettingReminder, setIsSettingReminder] = useState(false);
    const [aiMenuAnchor, setAiMenuAnchor] = useState(null);
    const [generatingAi, setGeneratingAi] = useState(false);
    const messagesEndRef = useRef(null);

    const API_BASE = '/api/twilio';

    useEffect(() => {
        if (open) {
            fetchConversations();
            fetchCallHistory();
            fetchContacts();
        }
    }, [open]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // AI Message Templates
    const AI_MESSAGE_TEMPLATES = {
        dropoff: {
            title: "Drop-off Follow-up",
            description: "Customer visited but didn't complete action",
            generatePrompt: (customerData) => `Generate a friendly follow-up message for ${customerData.name || 'the customer'} who visited our window tinting website and performed a price estimate but didn't complete the booking. Ask if we can assist them further. Keep it conversational and helpful.`
        },
        estimate_follow_up: {
            title: "Estimate Follow-up",
            description: "Following up on price estimate request",
            generatePrompt: (customerData) => `Generate a friendly follow-up message for ${customerData.name || 'the customer'} who requested a price estimate for window tinting. Ask if they have any questions about the estimate or if they'd like to proceed with booking. Mention our expertise and quality service.`
        },
        booking_reminder: {
            title: "Booking Reminder",
            description: "Remind customer about their booking",
            generatePrompt: (customerData) => `Generate a friendly reminder message for ${customerData.name || 'the customer'} about their upcoming window tinting appointment. Ask them to confirm and provide any last-minute instructions or questions.`
        },
        general_follow_up: {
            title: "General Follow-up",
            description: "General customer follow-up",
            generatePrompt: (customerData) => `Generate a friendly follow-up message for ${customerData.name || 'the customer'} who showed interest in our window tinting services. Ask how we can help them with their automotive or residential window tinting needs.`
        }
    };

    const fetchConversations = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE}/conversations`);
            setConversations(response.data);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (conversationSid) => {
        try {
            const response = await axios.get(`${API_BASE}/conversations/${conversationSid}/messages`);
            setMessages(response.data);
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const fetchCallHistory = async () => {
        try {
            const response = await axios.get(`${API_BASE}/calls`);
            setCallHistory(response.data);
        } catch (error) {
            console.error('Error fetching call history:', error);
        }
    };

    const fetchContacts = async () => {
        try {
            const response = await axios.get(`${API_BASE}/phone-numbers`);
            setContacts(response.data);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedConversation) return;

        try {
            await axios.post(`${API_BASE}/conversations/${selectedConversation.sid}/messages`, {
                body: newMessage
                // Note: author is now handled by the backend using standard address
            });
            setNewMessage('');
            fetchMessages(selectedConversation.sid);
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const makeCall = async (phoneNumber) => {
        try {
            const fromNumber = contacts[0]?.phoneNumber || '+1234567890'; // Use first available number
            await axios.post(`${API_BASE}/calls`, {
                to: phoneNumber,
                from: fromNumber
            });
            fetchCallHistory();
        } catch (error) {
            console.error('Error making call:', error);
        }
    };

    const createNewConversation = async () => {
        if (!newConversationNumber.trim()) return;

        try {
            const response = await axios.post(`${API_BASE}/conversations`, {
                phoneNumber: newConversationNumber,
                friendlyName: `Chat with ${newConversationNumber}`
            });
            setShowNewConversation(false);
            setNewConversationNumber('');
            fetchConversations();
            setSelectedConversation(response.data);
            fetchMessages(response.data.sid);
        } catch (error) {
            console.error('Error creating conversation:', error);
        }
    };

    const saveContactDetails = async () => {
        if (!contactDetails.phone.trim()) return;

        setIsCreatingContact(true);
        try {
            // This would connect to your backend to save contact details
            // For now, we'll just update the UI
            const phoneNumber = getParticipantPhoneNumber(selectedConversation);

            // In a real implementation, you would send this to your backend
            console.log('Saving contact details:', {
                ...contactDetails,
                phone: phoneNumber,
                conversationSid: selectedConversation.sid
            });

            // Update the conversation name if we have a contact name
            if (contactDetails.name && selectedConversation) {
                await axios.post(`${API_BASE}/conversations/${selectedConversation.sid}/update`, {
                    friendlyName: `Chat with ${contactDetails.name}`
                });

                // Refresh conversation list
                fetchConversations();

                // Update the selected conversation locally
                setSelectedConversation(prev => ({
                    ...prev,
                    friendlyName: `Chat with ${contactDetails.name}`
                }));
            }

            setShowContactModal(false);
        } catch (error) {
            console.error('Error saving contact details:', error);
        } finally {
            setIsCreatingContact(false);
        }
    };

    const saveReminderSettings = async () => {
        if (!reminderSettings.message.trim()) return;

        setIsSettingReminder(true);
        try {
            // This would connect to your backend to save reminder settings
            // For now, we'll just update the UI
            console.log('Saving reminder settings:', {
                ...reminderSettings,
                conversationSid: selectedConversation.sid,
                phoneNumber: getParticipantPhoneNumber(selectedConversation)
            });

            setShowReminderModal(false);
        } catch (error) {
            console.error('Error saving reminder settings:', error);
        } finally {
            setIsSettingReminder(false);
        }
    };

    const generateAiMessage = async (template) => {
        setGeneratingAi(true);
        setAiMenuAnchor(null);

        try {
            const phoneNumber = getParticipantPhoneNumber(selectedConversation);
            const customerName = selectedConversation.friendlyName?.replace('Chat with ', '') || 'Customer';

            const customerData = {
                name: customerName,
                phone: phoneNumber
            };

            // In a real implementation, you would call your AI service
            // For now, we'll use predefined templates
            const templateData = AI_MESSAGE_TEMPLATES[template];
            const prompt = templateData.generatePrompt(customerData);

            // Simulate AI response delay
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Generate a simple message based on the template
            let message = '';
            switch (template) {
                case 'dropoff':
                    message = `Hi ${customerName}! 👋 We noticed you were looking at window tinting options on our website. Did you have any questions about our services? We'd love to help you get the perfect tint for your vehicle! 🚗✨`;
                    break;
                case 'estimate_follow_up':
                    message = `Hello ${customerName}! Thanks for requesting a quote for window tinting. Our team has prepared your estimate. Do you have any questions about the pricing or our installation process? We're here to help! 😊`;
                    break;
                case 'booking_reminder':
                    message = `Hi ${customerName}! Just a friendly reminder about your upcoming window tinting appointment. Please let us know if you need to reschedule or have any questions before we get started! 🗓️`;
                    break;
                case 'general_follow_up':
                    message = `Hi ${customerName}! Thanks for your interest in Eclipse Tint. Whether it's automotive or residential window tinting, we're here to help with professional installation and quality films. What can we assist you with today? 🏠🚗`;
                    break;
                default:
                    message = `Hi ${customerName}! Thanks for contacting Eclipse Tint. How can we help you today?`;
            }

            // Set the new message text (doesn't send it automatically)
            setNewMessage(message);

        } catch (error) {
            console.error('Error generating AI message:', error);
        } finally {
            setGeneratingAi(false);
        }
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getParticipantPhoneNumber = (conversation) => {
        const participant = conversation.participants?.find(p => p.messagingBinding?.address);
        return participant?.messagingBinding?.address || participant?.identity || 'Unknown';
    };

    const isOwnMessage = (message, conversation) => {
        // For SMS conversations, messages from the Twilio proxy number are ours
        // Also handle legacy messages with our custom author
        return message.author === 'connect_eclipse@autofront.com.au' ||
            message.author === '+61489263516' ||
            !message.author; // Sometimes Twilio doesn't set author for proxy messages
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    height: '90vh',
                    backgroundColor: '#1a1a1a',
                    color: '#ffffff',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: '#ffffff',
                backgroundColor: '#2a2a2a',
                borderBottom: '1px solid #333',
                py: 1.5
            }}>
                <Typography variant="h6" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '1.1rem',
                    fontWeight: 600
                }}>
                    <MessageIcon sx={{ color: '#007AFF' }} />
                    Customer Communications
                </Typography>
                <IconButton onClick={onClose} sx={{ color: '#ffffff' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, height: 'calc(100% - 64px)', color: '#ffffff' }}>
                <Box sx={{ display: 'flex', height: '100%' }}>
                    {/* Left Sidebar */}
                    <Box sx={{
                        width: 320,
                        borderRight: '1px solid #333',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#222',
                        height: '100%'
                    }}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            sx={{
                                borderBottom: '1px solid #333',
                                '& .MuiTab-root': {
                                    color: '#9e9e9e',
                                    minHeight: '50px',
                                    '&.Mui-selected': {
                                        color: '#ffffff'
                                    }
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#007AFF'
                                }
                            }}
                        >
                            <Tab
                                label="Conversations"
                                icon={<MessageIcon fontSize="small" />}
                                iconPosition="start"
                                sx={{ fontSize: '13px', textTransform: 'none' }}
                            />
                            <Tab
                                label="Contacts"
                                icon={<ContactPhoneIcon fontSize="small" />}
                                iconPosition="start"
                                sx={{ fontSize: '13px', textTransform: 'none' }}
                            />
                            <Tab
                                label="Calls"
                                icon={<PhoneIcon fontSize="small" />}
                                iconPosition="start"
                                sx={{ fontSize: '13px', textTransform: 'none' }}
                            />
                        </Tabs>

                        {activeTab === 0 && (
                            <Box sx={{ flex: 1, overflow: 'auto' }}>
                                <Box sx={{ p: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => setShowNewConversation(true)}
                                        sx={{
                                            mb: 2,
                                            bgcolor: '#007AFF',
                                            color: '#ffffff',
                                            '&:hover': {
                                                bgcolor: '#0056CC'
                                            },
                                            borderRadius: '12px',
                                            py: 1.5,
                                            boxShadow: '0 4px 14px rgba(0, 122, 255, 0.4)'
                                        }}
                                    >
                                        New Conversation
                                    </Button>
                                </Box>
                                <List sx={{ px: 1 }}>
                                    {conversations.map((conv) => {
                                        const phoneNumber = getParticipantPhoneNumber(conv);
                                        const lastMessage = conv.recentMessages && conv.recentMessages.length > 0
                                            ? conv.recentMessages[0].body
                                            : 'No messages yet';

                                        return (
                                            <React.Fragment key={conv.sid}>
                                                <ListItem
                                                    button
                                                    selected={selectedConversation?.sid === conv.sid}
                                                    onClick={() => {
                                                        setSelectedConversation(conv);
                                                        fetchMessages(conv.sid);
                                                    }}
                                                    sx={{
                                                        borderRadius: '12px',
                                                        mb: 0.5,
                                                        transition: 'all 0.2s ease',
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'rgba(0, 122, 255, 0.15)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 122, 255, 0.25)'
                                                            }
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                                        }
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar sx={{
                                                            bgcolor: selectedConversation?.sid === conv.sid ? '#007AFF' : '#555',
                                                            color: '#fff'
                                                        }}>
                                                            <PersonIcon />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    color: '#e0e0e0',
                                                                    fontWeight: selectedConversation?.sid === conv.sid ? 'bold' : 'normal'
                                                                }}
                                                            >
                                                                {conv.friendlyName || phoneNumber}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: '#9e9e9e',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    maxWidth: '180px'
                                                                }}
                                                            >
                                                                {lastMessage}
                                                            </Typography>
                                                        }
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            sx={{ color: '#9e9e9e' }}
                                                            onClick={() => {
                                                                setSelectedConversation(conv);
                                                                makeCall(phoneNumber);
                                                            }}
                                                        >
                                                            <PhoneIcon />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                {conversations.indexOf(conv) < conversations.length - 1 && (
                                                    <Divider variant="inset" component="li" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}

                        {activeTab === 1 && (
                            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        sx={{
                                            bgcolor: '#007AFF',
                                            color: '#ffffff',
                                            '&:hover': {
                                                bgcolor: '#0056CC'
                                            },
                                            borderRadius: '12px',
                                            py: 1.5,
                                            boxShadow: '0 4px 14px rgba(0, 122, 255, 0.4)'
                                        }}
                                    >
                                        Add New Contact
                                    </Button>
                                </Box>
                                <List sx={{ px: 1 }}>
                                    {contacts.length === 0 ? (
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            py: 4
                                        }}>
                                            <ContactPhoneIcon sx={{ fontSize: 48, color: '#666', mb: 2 }} />
                                            <Typography variant="body2" sx={{ color: '#9e9e9e', textAlign: 'center' }}>
                                                No contacts available
                                            </Typography>
                                        </Box>
                                    ) : (
                                        contacts.map((contact, index) => (
                                            <React.Fragment key={index}>
                                                <ListItem
                                                    sx={{
                                                        borderRadius: '12px',
                                                        mb: 0.5,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                                        }
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ bgcolor: '#555', color: '#fff' }}>
                                                            <PersonIcon />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body1" sx={{ color: '#e0e0e0' }}>
                                                                {contact.friendlyName || 'Unnamed Contact'}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                                                                {contact.phoneNumber}
                                                            </Typography>
                                                        }
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Tooltip title="Send Message">
                                                            <IconButton
                                                                edge="end"
                                                                sx={{ color: '#007AFF', mr: 1 }}
                                                                onClick={() => {
                                                                    // Find existing conversation or create new one
                                                                    const existingConv = conversations.find(conv =>
                                                                        getParticipantPhoneNumber(conv) === contact.phoneNumber
                                                                    );

                                                                    if (existingConv) {
                                                                        setSelectedConversation(existingConv);
                                                                        fetchMessages(existingConv.sid);
                                                                        setActiveTab(0);
                                                                    } else {
                                                                        setNewConversationNumber(contact.phoneNumber);
                                                                        setShowNewConversation(true);
                                                                    }
                                                                }}
                                                            >
                                                                <MessageIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Call">
                                                            <IconButton
                                                                edge="end"
                                                                sx={{ color: '#4caf50' }}
                                                                onClick={() => makeCall(contact.phoneNumber)}
                                                            >
                                                                <PhoneIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                {index < contacts.length - 1 && (
                                                    <Divider variant="inset" component="li" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                                )}
                                            </React.Fragment>
                                        ))
                                    )}
                                </List>
                            </Box>
                        )}

                        {activeTab === 2 && (
                            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={fetchCallHistory}
                                        sx={{
                                            borderColor: '#666',
                                            color: '#9e9e9e',
                                            '&:hover': {
                                                borderColor: '#999',
                                                bgcolor: 'rgba(255, 255, 255, 0.05)'
                                            }
                                        }}
                                    >
                                        Refresh History
                                    </Button>
                                </Box>
                                <List sx={{ px: 1 }}>
                                    {callHistory.length === 0 ? (
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            py: 4
                                        }}>
                                            <PhoneIcon sx={{ fontSize: 48, color: '#666', mb: 2 }} />
                                            <Typography variant="body2" sx={{ color: '#9e9e9e', textAlign: 'center' }}>
                                                No call history available
                                            </Typography>
                                        </Box>
                                    ) : (
                                        callHistory.map((call, index) => (
                                            <React.Fragment key={call.sid || index}>
                                                <ListItem
                                                    sx={{
                                                        borderRadius: '12px',
                                                        mb: 0.5,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                                        }
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar sx={{
                                                            bgcolor: call.direction === 'outbound-api' ? '#4caf50' : '#f44336',
                                                            color: '#fff'
                                                        }}>
                                                            <PhoneIcon />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body1" sx={{ color: '#e0e0e0' }}>
                                                                {call.to}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                                                                    {call.status} - {formatDate(call.dateCreated)} {formatTime(call.dateCreated)}
                                                                </Typography>
                                                                <Chip
                                                                    label={call.direction === 'outbound-api' ? 'Outbound' : 'Inbound'}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: call.direction === 'outbound-api' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                                                        color: call.direction === 'outbound-api' ? '#4caf50' : '#f44336',
                                                                        borderRadius: '12px',
                                                                        height: '20px'
                                                                    }}
                                                                />
                                                            </Box>
                                                        }
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Tooltip title="Call Back">
                                                            <IconButton
                                                                edge="end"
                                                                sx={{ color: '#4caf50' }}
                                                                onClick={() => makeCall(call.to)}
                                                            >
                                                                <PhoneIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                {index < callHistory.length - 1 && (
                                                    <Divider variant="inset" component="li" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                                )}
                                            </React.Fragment>
                                        ))
                                    )}
                                </List>
                            </Box>
                        )}
                    </Box>

                    {/* Main Chat Area */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#121212' }}>
                        {selectedConversation ? (
                            <>
                                {/* Chat Header */}
                                <Box sx={{ p: 2, borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar sx={{ bgcolor: '#007AFF', width: 36, height: 36 }}>
                                            <PersonIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6">
                                                {selectedConversation.friendlyName || getParticipantPhoneNumber(selectedConversation)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
                                                {getParticipantPhoneNumber(selectedConversation)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Tooltip title="Edit Contact Details">
                                            <IconButton
                                                onClick={() => setShowContactModal(true)}
                                                sx={{ color: '#9e9e9e' }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Set Reminder">
                                            <IconButton
                                                onClick={() => setShowReminderModal(true)}
                                                sx={{ color: '#9e9e9e' }}
                                            >
                                                <NotificationsIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Call">
                                            <IconButton
                                                onClick={() => makeCall(getParticipantPhoneNumber(selectedConversation))}
                                                sx={{ color: '#4caf50' }}
                                            >
                                                <PhoneIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Messages */}
                                <Box sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    bgcolor: '#000000',
                                    backgroundImage: `
                                        radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.02) 0%, transparent 50%),
                                        radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
                                    `,
                                    m: 0.5,
                                    borderRadius: '8px',
                                    p: 2
                                }}>
                                    {messages.length === 0 ? (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            flexDirection: 'column',
                                            gap: 1
                                        }}>
                                            <MessageIcon sx={{ fontSize: 48, color: '#666' }} />
                                            <Typography variant="body2" sx={{ color: '#8e8e93', textAlign: 'center' }}>
                                                No messages yet
                                                <br />
                                                Start a conversation to see messages here
                                            </Typography>
                                        </Box>
                                    ) : (
                                        messages.map((message, index) => {
                                            const isOwn = isOwnMessage(message, selectedConversation);
                                            const prevMessage = index > 0 ? messages[index - 1] : null;
                                            const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;
                                            const showTime = !nextMessage ||
                                                isOwnMessage(nextMessage, selectedConversation) !== isOwn ||
                                                (new Date(message.dateCreated).getTime() - new Date(nextMessage.dateCreated).getTime()) > 300000; // 5 minutes

                                            return (
                                                <Box
                                                    key={message.sid || index}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: isOwn ? 'flex-end' : 'flex-start',
                                                        mb: showTime ? 1.5 : 0.5,
                                                        px: 1
                                                    }}
                                                >
                                                    <Box sx={{
                                                        position: 'relative',
                                                        maxWidth: '70%',
                                                        minWidth: '60px'
                                                    }}>
                                                        {/* Message Bubble with Integrated Tail */}
                                                        <Box sx={{
                                                            position: 'relative',
                                                            bgcolor: isOwn ? '#007AFF' : '#333333',
                                                            color: '#ffffff',
                                                            borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                            px: 2,
                                                            py: 1.5,
                                                            wordBreak: 'break-word',
                                                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                                                        }}>
                                                            <Typography variant="body2" sx={{
                                                                fontSize: '14px',
                                                                lineHeight: 1.4,
                                                                fontWeight: 400
                                                            }}>
                                                                {message.body}
                                                            </Typography>

                                                            {/* Tail extending from bubble */}
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                bottom: 0,
                                                                ...(isOwn ? { right: -8 } : { left: -8 }),
                                                                width: 0,
                                                                height: 0,
                                                                borderStyle: 'solid',
                                                                ...(isOwn ? {
                                                                    borderWidth: '0 0 10px 8px',
                                                                    borderColor: 'transparent transparent transparent #007AFF'
                                                                } : {
                                                                    borderWidth: '0 8px 10px 0',
                                                                    borderColor: 'transparent #333333 transparent transparent'
                                                                })
                                                            }} />
                                                        </Box>

                                                        {/* Timestamp */}
                                                        {showTime && (
                                                            <Typography variant="caption" sx={{
                                                                display: 'block',
                                                                textAlign: isOwn ? 'right' : 'left',
                                                                color: '#8e8e93',
                                                                fontSize: '11px',
                                                                mt: 0.5,
                                                                px: 1
                                                            }}>
                                                                {formatTime(message.dateCreated)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </Box>

                                {/* Message Input */}
                                <Box sx={{ p: 2, borderTop: '1px solid #333', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {/* AI Message Button */}
                                    <Box sx={{ mb: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={generatingAi ? <CircularProgress size={16} /> : <BotIcon />}
                                            onClick={(e) => setAiMenuAnchor(e.currentTarget)}
                                            disabled={generatingAi}
                                            fullWidth
                                            sx={{
                                                textTransform: 'none',
                                                borderColor: '#666',
                                                color: '#9e9e9e',
                                                borderRadius: '20px',
                                                py: 1.5,
                                                '&:hover': {
                                                    borderColor: '#999',
                                                    bgcolor: 'rgba(255, 255, 255, 0.02)'
                                                }
                                            }}
                                        >
                                            {generatingAi ? 'Generating...' : 'AI Message Templates'}
                                        </Button>

                                        <Menu
                                            anchorEl={aiMenuAnchor}
                                            open={Boolean(aiMenuAnchor)}
                                            onClose={() => setAiMenuAnchor(null)}
                                            PaperProps={{
                                                sx: {
                                                    bgcolor: '#2a2a2a',
                                                    border: '1px solid #333',
                                                    borderRadius: '12px'
                                                }
                                            }}
                                        >
                                            {Object.entries(AI_MESSAGE_TEMPLATES).map(([key, template]) => (
                                                <MenuItem
                                                    key={key}
                                                    onClick={() => generateAiMessage(key)}
                                                    sx={{
                                                        color: '#e0e0e0',
                                                        borderRadius: '8px',
                                                        mx: 1,
                                                        my: 0.5
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {template.title}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
                                                            {template.description}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </Box>

                                    {/* Text Input Area */}
                                    <Box sx={{
                                        bgcolor: '#1a1a1a',
                                        borderRadius: '24px',
                                        border: '1px solid #666',
                                        p: 1,
                                        '&:focus-within': {
                                            borderColor: '#007AFF',
                                            boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.2)'
                                        },
                                        transition: 'all 0.2s ease'
                                    }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            maxRows={5}
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            placeholder="Type your message..."
                                            variant="standard"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    sendMessage();
                                                }
                                            }}
                                            sx={{
                                                '& .MuiInput-root': {
                                                    color: '#e0e0e0',
                                                    fontSize: '16px',
                                                    '&:before': { display: 'none' },
                                                    '&:after': { display: 'none' }
                                                },
                                                '& .MuiInput-input': {
                                                    padding: '8px 16px',
                                                    borderRadius: '20px',
                                                    '&::placeholder': {
                                                        color: '#8e8e93',
                                                        opacity: 1
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>

                                    {/* Send Button */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <IconButton
                                            onClick={sendMessage}
                                            disabled={!newMessage.trim()}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                bgcolor: newMessage.trim() ? '#007AFF' : '#666',
                                                color: '#ffffff',
                                                borderRadius: '24px',
                                                '&:hover': {
                                                    bgcolor: newMessage.trim() ? '#0056CC' : '#666',
                                                    transform: 'scale(1.05)'
                                                },
                                                '&.Mui-disabled': {
                                                    bgcolor: '#666',
                                                    color: '#999'
                                                },
                                                transition: 'all 0.2s ease',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                                            }}
                                        >
                                            <SendIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 2
                            }}>
                                <MessageIcon sx={{ fontSize: 64, color: '#666' }} />
                                <Typography variant="h6" sx={{ color: '#9e9e9e', textAlign: 'center' }}>
                                    Select a conversation to start messaging
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowNewConversation(true)}
                                    sx={{
                                        mt: 2,
                                        bgcolor: '#007AFF',
                                        color: '#ffffff',
                                        '&:hover': {
                                            bgcolor: '#0056CC'
                                        },
                                        borderRadius: '12px',
                                        py: 1.5,
                                        px: 3,
                                        boxShadow: '0 4px 14px rgba(0, 122, 255, 0.4)'
                                    }}
                                >
                                    New Conversation
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* New Conversation Dialog */}
                <Dialog
                    open={showNewConversation}
                    onClose={() => setShowNewConversation(false)}
                    PaperProps={{
                        sx: {
                            bgcolor: '#1a1a1a',
                            color: '#ffffff',
                            borderRadius: '12px',
                            maxWidth: '500px'
                        }
                    }}
                >
                    <DialogTitle sx={{ borderBottom: '1px solid #333', bgcolor: '#2a2a2a' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MessageIcon />
                                Start New Conversation
                            </Typography>
                            <IconButton onClick={() => setShowNewConversation(false)} sx={{ color: '#ffffff' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ py: 3 }}>
                        <Typography variant="body2" sx={{ color: '#9e9e9e', mb: 2 }}>
                            Enter a phone number to start a new conversation. The number should be in international format.
                        </Typography>
                        <TextField
                            autoFocus
                            margin="normal"
                            label="Phone Number"
                            type="tel"
                            fullWidth
                            variant="outlined"
                            value={newConversationNumber}
                            onChange={(e) => setNewConversationNumber(e.target.value)}
                            placeholder="+1234567890"
                            InputLabelProps={{ sx: { color: '#9e9e9e' } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#ffffff',
                                    '& fieldset': { borderColor: '#666' },
                                    '&:hover fieldset': { borderColor: '#888' },
                                    '&.Mui-focused fieldset': { borderColor: '#007AFF' },
                                },
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: '1px solid #333' }}>
                        <Button
                            onClick={() => setShowNewConversation(false)}
                            sx={{ color: '#9e9e9e' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={createNewConversation}
                            variant="contained"
                            disabled={!newConversationNumber.trim()}
                            sx={{
                                bgcolor: '#007AFF',
                                '&:hover': {
                                    bgcolor: '#0056CC'
                                },
                                borderRadius: '8px'
                            }}
                        >
                            Start Chat
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Contact Details Modal */}
                <Dialog
                    open={showContactModal}
                    onClose={() => setShowContactModal(false)}
                    PaperProps={{
                        sx: {
                            bgcolor: '#1a1a1a',
                            color: '#ffffff',
                            borderRadius: '12px',
                            maxWidth: '500px'
                        }
                    }}
                >
                    <DialogTitle sx={{ borderBottom: '1px solid #333', bgcolor: '#2a2a2a' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon />
                                Contact Details
                            </Typography>
                            <IconButton onClick={() => setShowContactModal(false)} sx={{ color: '#ffffff' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ py: 3 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={contactDetails.name}
                            onChange={(e) => setContactDetails({ ...contactDetails, name: e.target.value })}
                            margin="normal"
                            variant="outlined"
                            InputLabelProps={{ sx: { color: '#9e9e9e' } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#ffffff',
                                    '& fieldset': { borderColor: '#666' },
                                    '&:hover fieldset': { borderColor: '#888' },
                                    '&.Mui-focused fieldset': { borderColor: '#2196f3' },
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Phone"
                            value={contactDetails.phone}
                            onChange={(e) => setContactDetails({ ...contactDetails, phone: e.target.value })}
                            margin="normal"
                            variant="outlined"
                            disabled
                            InputLabelProps={{ sx: { color: '#9e9e9e' } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#999',
                                    '& fieldset': { borderColor: '#666' },
                                    '&:hover fieldset': { borderColor: '#888' },
                                    '&.Mui-focused fieldset': { borderColor: '#2196f3' },
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Email"
                            value={contactDetails.email}
                            onChange={(e) => setContactDetails({ ...contactDetails, email: e.target.value })}
                            margin="normal"
                            variant="outlined"
                            InputLabelProps={{ sx: { color: '#9e9e9e' } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#ffffff',
                                    '& fieldset': { borderColor: '#666' },
                                    '&:hover fieldset': { borderColor: '#888' },
                                    '&.Mui-focused fieldset': { borderColor: '#2196f3' },
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Notes"
                            value={contactDetails.notes}
                            onChange={(e) => setContactDetails({ ...contactDetails, notes: e.target.value })}
                            margin="normal"
                            variant="outlined"
                            multiline
                            rows={3}
                            InputLabelProps={{ sx: { color: '#9e9e9e' } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#ffffff',
                                    '& fieldset': { borderColor: '#666' },
                                    '&:hover fieldset': { borderColor: '#888' },
                                    '&.Mui-focused fieldset': { borderColor: '#2196f3' },
                                },
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: '1px solid #333' }}>
                        <Button
                            onClick={() => setShowContactModal(false)}
                            sx={{ color: '#9e9e9e' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveContactDetails}
                            variant="contained"
                            disabled={isCreatingContact}
                            sx={{
                                bgcolor: '#007AFF',
                                '&:hover': {
                                    bgcolor: '#0056CC'
                                },
                                borderRadius: '8px'
                            }}
                        >
                            {isCreatingContact ? <CircularProgress size={24} /> : 'Save Contact'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Reminder Settings Modal */}
                <Dialog
                    open={showReminderModal}
                    onClose={() => setShowReminderModal(false)}
                    PaperProps={{
                        sx: {
                            bgcolor: '#1a1a1a',
                            color: '#ffffff',
                            borderRadius: '12px',
                            maxWidth: '500px'
                        }
                    }}
                >
                    <DialogTitle sx={{ borderBottom: '1px solid #333', bgcolor: '#2a2a2a' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <NotificationsIcon />
                                Reminder Settings
                            </Typography>
                            <IconButton onClick={() => setShowReminderModal(false)} sx={{ color: '#ffffff' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ py: 3 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={reminderSettings.enabled}
                                    onChange={(e) => setReminderSettings({
                                        ...reminderSettings,
                                        enabled: e.target.checked
                                    })}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#007AFF',
                                            '& + .MuiSwitch-track': {
                                                backgroundColor: '#007AFF',
                                            },
                                        },
                                    }}
                                />
                            }
                            label="Enable follow-up reminder"
                            sx={{ mb: 2, color: '#e0e0e0' }}
                        />

                        <FormControl
                            fullWidth
                            margin="normal"
                            disabled={!reminderSettings.enabled}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#ffffff',
                                    '& fieldset': { borderColor: '#666' },
                                    '&:hover fieldset': { borderColor: '#888' },
                                    '&.Mui-focused fieldset': { borderColor: '#2196f3' },
                                },
                                '& .MuiFormLabel-root': {
                                    color: '#9e9e9e',
                                },
                            }}
                        >
                            <InputLabel id="reminder-interval-label">Remind after</InputLabel>
                            <Select
                                labelId="reminder-interval-label"
                                value={reminderSettings.interval}
                                label="Remind after"
                                onChange={(e) => setReminderSettings({
                                    ...reminderSettings,
                                    interval: e.target.value
                                })}
                            >
                                <MenuItem value="4">4 hours</MenuItem>
                                <MenuItem value="8">8 hours</MenuItem>
                                <MenuItem value="24">1 day</MenuItem>
                                <MenuItem value="48">2 days</MenuItem>
                                <MenuItem value="72">3 days</MenuItem>
                                <MenuItem value="168">1 week</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl
                            fullWidth
                            margin="normal"
                            disabled={!reminderSettings.enabled}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#ffffff',
                                    '& fieldset': { borderColor: '#666' },
                                    '&:hover fieldset': { borderColor: '#888' },
                                    '&.Mui-focused fieldset': { borderColor: '#2196f3' },
                                },
                                '& .MuiFormLabel-root': {
                                    color: '#9e9e9e',
                                },
                            }}
                        >
                            <InputLabel id="reminder-type-label">Reminder Type</InputLabel>
                            <Select
                                labelId="reminder-type-label"
                                value={reminderSettings.type}
                                label="Reminder Type"
                                onChange={(e) => setReminderSettings({
                                    ...reminderSettings,
                                    type: e.target.value
                                })}
                            >
                                <MenuItem value="manual">Manual (notify me only)</MenuItem>
                                <MenuItem value="auto">Automatic (send message)</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Follow-up Message"
                            value={reminderSettings.message}
                            onChange={(e) => setReminderSettings({
                                ...reminderSettings,
                                message: e.target.value
                            })}
                            margin="normal"
                            variant="outlined"
                            multiline
                            rows={4}
                            disabled={!reminderSettings.enabled || reminderSettings.type !== 'auto'}
                            InputLabelProps={{ sx: { color: '#9e9e9e' } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#ffffff',
                                    '& fieldset': { borderColor: '#666' },
                                    '&:hover fieldset': { borderColor: '#888' },
                                    '&.Mui-focused fieldset': { borderColor: '#2196f3' },
                                },
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: '1px solid #333' }}>
                        <Button
                            onClick={() => setShowReminderModal(false)}
                            sx={{ color: '#9e9e9e' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveReminderSettings}
                            variant="contained"
                            disabled={isSettingReminder || !reminderSettings.enabled}
                            sx={{
                                bgcolor: '#007AFF',
                                '&:hover': {
                                    bgcolor: '#0056CC'
                                },
                                borderRadius: '8px'
                            }}
                        >
                            {isSettingReminder ? <CircularProgress size={24} /> : 'Save Reminder'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </DialogContent>
        </Dialog>
    );
};

export default TwilioConversations;